import {
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Mesh,
  ShaderMaterial,
  type Scene,
} from 'three';
import { PALETTE } from '@/config';
import { RNG } from '@/utils/rng';
import { Noise } from '@/utils/noise';
import { TAU, lerp } from '@/utils/math';

/**
 * 水墨遠山 — the layered distant ranges.
 *
 * Two or three static silhouette rings encircle the origin at increasing radii.
 * Each ring is a single closed "curtain" of geometry whose top edge is an fbm
 * ridgeline sampled from the seeded Noise (so the whole thing is deterministic and
 * reproducible across save/load). Nearer rings are taller and craggier; farther
 * rings sit low and smooth, fading toward paper — the classic recession of an
 * ink-wash horizon.
 *
 * Each ring is one merged BufferGeometry + one unlit ShaderMaterial (no lighting,
 * no fog, no per-frame vertex work): the top of the curtain carries dense ink that
 * dissolves into mist toward the base, so the ranges appear to float on the horizon
 * like a wash rather than sit on the ground. They never move — `update()` only
 * refreshes the mood/night uniforms, which darken them toward ink at night and
 * desaturate them when the city is bleak.
 */

interface RingSpec {
  radius: number;
  base: number; // mean ridge height (m)
  amp: number; // ridge relief (m)
  freq: number; // ridgeline noise frequency
  octaves: number; // craggier when higher
  segments: number; // horizontal tessellation
}

// Inner -> outer. Nearer rings taller & rougher, farther rings low & smooth.
const RINGS: readonly RingSpec[] = [
  { radius: 460, base: 62, amp: 30, freq: 0.0065, octaves: 5, segments: 260 },
  { radius: 550, base: 44, amp: 20, freq: 0.0052, octaves: 4, segments: 240 },
  { radius: 640, base: 30, amp: 13, freq: 0.0040, octaves: 3, segments: 220 },
];

// How far the curtain drops below the horizon so there is never a gap under it.
const BASE_DROP = 30;

export class Mountains {
  private readonly meshes: Mesh[] = [];
  private readonly materials: ShaderMaterial[] = [];

  constructor(scene: Scene, rng: RNG) {
    for (let r = 0; r < RINGS.length; r++) {
      const spec = RINGS[r];
      // Each ring gets its own independent noise field so ridgelines never rhyme.
      const noise = new Noise(rng.fork(r + 1));
      const geo = this.buildRing(spec, noise);

      // Recede from ink (inner) toward paper (outer) across the rings.
      const t = RINGS.length > 1 ? r / (RINGS.length - 1) : 0;
      const inkCol = new Color(PALETTE.mountain).lerp(new Color(PALETTE.mountainFar), t);
      const mistCol = inkCol.clone().lerp(new Color(PALETTE.fog), 0.82);
      const maxAlpha = lerp(0.95, 0.55, t); // outer rings are fainter

      const mat = new ShaderMaterial({
        transparent: true,
        depthWrite: false,
        // Beyond the city but in front of the sky dome; draw as a flat silhouette.
        side: DoubleSide,
        fog: false,
        uniforms: {
          uInk: { value: inkCol },
          uMist: { value: mistCol },
          uNightInk: { value: new Color(PALETTE.accent) },
          uMaxAlpha: { value: maxAlpha },
          uMood: { value: 1 },
          uNight: { value: 0 },
        },
        vertexShader: /* glsl */ `
          attribute float aEdge; // 0 at the base, 1 at the ridge top
          varying float vEdge;
          void main() {
            vEdge = aEdge;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          precision highp float;
          varying float vEdge;
          uniform vec3 uInk, uMist, uNightInk;
          uniform float uMaxAlpha, uMood, uNight;

          void main() {
            // Vertical wash: ink concentrated at the ridge, dissolving to mist
            // toward the base so the range floats on the horizon.
            float ink = smoothstep(0.0, 1.0, vEdge);
            vec3 col = mix(uMist, uInk, ink);

            // Night pulls the whole silhouette toward flat ink.
            col = mix(col, uNightInk, uNight * 0.6);

            // Mood desaturates & darkens the ink when the city is bleak.
            float lum = dot(col, vec3(0.299, 0.587, 0.114));
            col = mix(vec3(lum), col, mix(0.65, 1.0, uMood));
            col *= mix(0.9, 1.0, uMood);

            // Alpha fades out at the very base so there is no hard bottom edge.
            float alpha = smoothstep(0.02, 0.55, vEdge) * uMaxAlpha;
            gl_FragColor = vec4(col, alpha);
          }
        `,
      });

      const mesh = new Mesh(geo, mat);
      mesh.frustumCulled = false;
      // Behind the city/ground (renderOrder 0 / -1) but over the sky (-1000).
      mesh.renderOrder = -900 + r; // inner rings drawn last so they sit on top
      scene.add(mesh);

      this.meshes.push(mesh);
      this.materials.push(mat);
    }
  }

  /**
   * Build one closed curtain: a strip of quads around the full circle, its top
   * edge displaced by an fbm ridgeline and its bottom edge sunk below the horizon.
   * Sampling the noise at the true (x,z) of each column makes the ridgeline wrap
   * seamlessly, because the last column coincides with the first.
   */
  private buildRing(spec: RingSpec, noise: Noise): BufferGeometry {
    const { radius, base, amp, freq, octaves, segments } = spec;
    const positions: number[] = [];
    const edges: number[] = [];
    const indices: number[] = [];

    for (let i = 0; i <= segments; i++) {
      const a = (i / segments) * TAU;
      const x = Math.cos(a) * radius;
      const z = Math.sin(a) * radius;
      // fbm in [-1,1] -> ridge height. abs() keeps peaks rising from the base.
      const n = noise.fbm2D(x * freq, z * freq, octaves);
      const top = base + amp * (n * 0.5 + 0.5);

      // Bottom vertex then top vertex for this column.
      positions.push(x, -BASE_DROP, z);
      edges.push(0);
      positions.push(x, top, z);
      edges.push(1);
    }

    // Two triangles per adjacent pair of columns.
    for (let i = 0; i < segments; i++) {
      const b0 = i * 2; // bottom of column i
      const t0 = b0 + 1; // top of column i
      const b1 = b0 + 2; // bottom of column i+1
      const t1 = b0 + 3; // top of column i+1
      indices.push(b0, t0, t1);
      indices.push(b0, t1, b1);
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('aEdge', new Float32BufferAttribute(edges, 1));
    geo.setIndex(indices);
    return geo;
  }

  update(mood: number, night: number): void {
    for (const mat of this.materials) {
      mat.uniforms.uMood.value = mood;
      mat.uniforms.uNight.value = night;
    }
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      mesh.geometry.dispose();
      mesh.removeFromParent();
    }
    for (const mat of this.materials) mat.dispose();
    this.meshes.length = 0;
    this.materials.length = 0;
  }
}
